/**
 * A tag is a label a user creates, never a seeded list (piece 13, decisions
 * 0186 and 0226). 🔨 A tag never gates access and never drives a rule: it
 * filters a list and groups reports, and do-not-contact stays a tag the
 * Outbound Mail Service reads.
 */
import type { IconMeaning } from '../icons';
import { normaliseText } from '../records/normalise';
import type { TaggableRecordType } from '../taggedRecord';

export class TagError extends Error {}

/**
 * The tag palette (piece 1): eight named swatches, stored by token name the way
 * `PersonRecord.avatarColor` is, never as a hex. 🔨 The palette is deliberately
 * not the four status groups — a tag is a label a person coins, and a chip drawn
 * in a status pair would read as a state the record is in.
 */
export const TAG_COLOURS = ['tag-slate', 'tag-blue', 'tag-teal', 'tag-green', 'tag-amber', 'tag-orange', 'tag-red', 'tag-purple'] as const;
export type TagColour = (typeof TAG_COLOURS)[number];

/** What a tag nobody has coloured draws; a palette entry, never a value of its own. */
export const DEFAULT_TAG_COLOUR: TagColour = 'tag-slate';

export function isTagColour(value: unknown): value is TagColour {
  return typeof value === 'string' && (TAG_COLOURS as readonly string[]).includes(value);
}

/** A stored colour resolved for drawing: an absent or unknown name falls back to the default. */
export function tagColourOrDefault(value: unknown): TagColour {
  return isTagColour(value) ? value : DEFAULT_TAG_COLOUR;
}

/**
 * The colour a write may store: null for "no colour chosen", or a palette name.
 * 🔨 Anything else is refused rather than quietly defaulted, so a hex typed at
 * the API is an error the caller sees and not a silent grey.
 */
export function tagColourForWrite(value: unknown): TagColour | null {
  if (value === null || value === undefined || value === '') return null;
  if (!isTagColour(value)) throw new TagError('a tag color is one of the palette swatches, never a hex');
  return value;
}

export interface TagRow {
  id: string;
  label: string;
  normalisedLabel: string;
  colour?: string | null;
  followUpThresholdDays?: number | null;
  isActive?: boolean;
}

/** Uniqueness and reuse-by-name compare the case-folded label, never the typed one. */
export function normaliseLabel(label: string): string {
  const normalised = normaliseText(label);
  if (!normalised) throw new TagError('a tag carries a label');
  return normalised;
}

/** Adding a tag offers existing labels first and creates a new one on Enter. */
export function matchingTags(tags: readonly TagRow[], typed: string): TagRow[] {
  const term = normaliseText(typed);
  if (!term) return [];
  return tags.filter((tag) => tag.isActive !== false && tag.normalisedLabel.includes(term));
}

export function existingTag(tags: readonly TagRow[], typed: string): TagRow | null {
  const normalised = normaliseText(typed);
  return tags.find((tag) => tag.normalisedLabel === normalised && tag.isActive !== false) ?? null;
}

/** One row per (record, tag); adding the same tag twice to one record is a no-op. */
export function alreadyTagged(rows: readonly { tagId: string; taggedRecordType: string; taggedRecordId: string }[], tagId: string, recordType: TaggableRecordType, recordId: string): boolean {
  return rows.some((row) => row.tagId === tagId && row.taggedRecordType === recordType && row.taggedRecordId === recordId);
}

/**
 * 🔨 A tag never gates access and never drives a rule. The one reader that takes
 * a number from it is the follow-up chain, and where two tags on one record both
 * carry one the smaller wins (decision 0209).
 */
export function followUpThresholdFrom(tags: readonly TagRow[]): number | null {
  const days = tags.filter((t) => t.isActive !== false).map((t) => t.followUpThresholdDays).filter((d): d is number => typeof d === 'number');
  return days.length === 0 ? null : Math.min(...days);
}

/** Merging two labels re-points their rows and retires one; retired labels are never deleted. */
export function mergePlan(losing: TagRow, winning: TagRow): { repointTagId: string; toTagId: string; retire: string } {
  if (losing.id === winning.id) throw new TagError('a tag cannot be merged into itself');
  return { repointTagId: losing.id, toTagId: winning.id, retire: losing.id };
}

/**
 * 🔒 A tag category's icon (decision 2260): one of sixteen Lucide glyphs that
 * mean nothing else in piece 1's icon reference, award excepted: it also draws a
 * site's Tax Exempt certificate badge (decision 7390). The column stores the glyph's
 * name and the database refuses any other, as it refuses a hex on a tag.
 */
export const TAG_CATEGORY_ICONS = ['award', 'layers', 'cpu', 'map', 'megaphone', 'gauge', 'alarm-clock', 'tag', 'zap', 'globe', 'thermometer', 'flame', 'leaf', 'gem', 'bookmark', 'compass'] as const;
export type TagCategoryIcon = (typeof TAG_CATEGORY_ICONS)[number];

/** The icon reference's meaning for each category glyph, so the web draws it through the one Icon. */
export const TAG_CATEGORY_ICON_MEANINGS: Record<TagCategoryIcon, IconMeaning> = {
  award: 'tagCategoryAward',
  layers: 'tagCategoryLayers',
  cpu: 'tagCategoryCpu',
  map: 'tagCategoryMap',
  megaphone: 'tagCategoryMegaphone',
  gauge: 'tagCategoryGauge',
  'alarm-clock': 'tagCategoryAlarmClock',
  tag: 'tagCategoryTag',
  zap: 'tagCategoryZap',
  globe: 'tagCategoryGlobe',
  thermometer: 'tagCategoryThermometer',
  flame: 'tagCategoryFlame',
  leaf: 'tagCategoryLeaf',
  gem: 'tagCategoryGem',
  bookmark: 'tagCategoryBookmark',
  compass: 'tagCategoryCompass',
};

export function isTagCategoryIcon(value: unknown): value is TagCategoryIcon {
  return typeof value === 'string' && (TAG_CATEGORY_ICONS as readonly string[]).includes(value);
}

/** 🔒 A note on a record's tag is up to this many characters (decision 2261). */
export const TAG_NOTE_MAX_LENGTH = 500;

/** 🔒 Past this many tags a banner draws the grouped strip, one line per category (decision 2260). */
export const TAG_GROUPED_STRIP_AFTER = 8;

/** 🔒 A Deal's contact row draws this many of the person's categorized tags, and a +N for the rest (decision 2262). */
export const DEAL_CONTACT_TAGS_SHOWN = 4;

/** The note a write stores: trimmed, null for blank, refused past the limit (decision 2261). */
export function tagNoteForWrite(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') throw new TagError('A tag note is text.');
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (trimmed.length > TAG_NOTE_MAX_LENGTH) throw new TagError(`A tag note is ${TAG_NOTE_MAX_LENGTH} characters or fewer.`);
  return trimmed;
}

/** One category as every reader receives it (decision 2260). */
export interface TagCategoryView {
  id: string;
  name: string;
  icon: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
}

/** A category write, checked before it reaches the database: a name, one of the sixteen icons, one of the eight swatches. */
export function tagCategoryForWrite(input: { name?: unknown; icon?: unknown; color?: unknown }): { name: string; normalisedName: string; icon: TagCategoryIcon; color: TagColour } {
  const name = typeof input.name === 'string' ? input.name.trim() : '';
  const normalisedName = normaliseText(name);
  if (!normalisedName) throw new TagError('A tag category carries a name.');
  if (!isTagCategoryIcon(input.icon)) throw new TagError('A tag category icon is one of the sixteen offered.');
  if (!isTagColour(input.color)) throw new TagError('A tag category color is one of the eight swatches, never a hex.');
  return { name, normalisedName, icon: input.icon, color: input.color };
}

/**
 * One tag as a pill draws it: its label, its own color, the record's note on it
 * where the read is for one record, and its category. 🔒 The category travels
 * whether or not it is in use, and the pill decides (decision 2260).
 */
export interface DrawnTag {
  id: string;
  label: string;
  colour?: string | null;
  note?: string | null;
  category?: TagCategoryView | null;
}

/** The category a pill draws under: its own while that category is in use, none otherwise. */
export function categoryInUse(tag: DrawnTag): TagCategoryView | null {
  return tag.category && tag.category.isActive ? tag.category : null;
}

/**
 * 🔒 What a pill draws (decision 2260): a tag in a category in use takes the
 * category's color and icon; any other draws the plain tag icon in its own
 * color, the default swatch where it has none.
 */
export function tagLook(tag: DrawnTag): { colour: TagColour; icon: TagCategoryIcon; categoryName: string | null } {
  const category = categoryInUse(tag);
  if (category) return { colour: tagColourOrDefault(category.color), icon: isTagCategoryIcon(category.icon) ? category.icon : 'tag', categoryName: category.name };
  return { colour: tagColourOrDefault(tag.colour), icon: 'tag', categoryName: null };
}

/**
 * 🔒 The pill's tooltip (decisions 2260, 2261): *Category: Tag*, *No category:
 * Tag* for one with none, and the record's note as the second line.
 */
export function tagTooltip(tag: DrawnTag, noCategoryLabel: string): string {
  const head = `${tagLook(tag).categoryName ?? noCategoryLabel}: ${tag.label}`;
  return tag.note ? `${head}\n${tag.note}` : head;
}

/** One group of a record's tags: its category, or null for the uncategorized. */
export interface TagGroup<T extends DrawnTag = DrawnTag> {
  category: TagCategoryView | null;
  tags: T[];
}

/**
 * 🔒 A record's tags grouped by category in the Settings order, the
 * uncategorized last, each group's tags alphabetical (decision 2260). A tag
 * whose category is out of use falls into the uncategorized group.
 */
export function groupTagsByCategory<T extends DrawnTag>(tags: readonly T[]): TagGroup<T>[] {
  const groups = new Map<string, TagGroup<T>>();
  const loose: T[] = [];
  for (const tag of tags) {
    const category = categoryInUse(tag);
    if (!category) {
      loose.push(tag);
      continue;
    }
    const group = groups.get(category.id) ?? { category, tags: [] };
    group.tags.push(tag);
    groups.set(category.id, group);
  }
  const byLabel = (a: T, b: T) => a.label.localeCompare(b.label);
  const ordered = [...groups.values()].sort((a, b) => a.category!.sortOrder - b.category!.sortOrder || a.category!.name.localeCompare(b.category!.name));
  for (const group of ordered) group.tags.sort(byLabel);
  return loose.length === 0 ? ordered : [...ordered, { category: null, tags: [...loose].sort(byLabel) }];
}

/** The tags in the order the banner draws them: the groups flattened. */
export function orderedTags<T extends DrawnTag>(tags: readonly T[]): T[] {
  return groupTagsByCategory(tags).flatMap((group) => group.tags);
}

/** The banner draws the grouped strip past eight tags (decision 2260). */
export function drawsGroupedStrip(count: number): boolean {
  return count > TAG_GROUPED_STRIP_AFTER;
}

/**
 * 🔒 A Deal contact row's tags (decision 2262): the person's tags in a category
 * in use, in the banner's order, the first four drawn and the rest counted.
 */
export function dealContactTags<T extends DrawnTag>(tags: readonly T[]): { shown: T[]; more: number } {
  const categorized = orderedTags(tags.filter((tag) => categoryInUse(tag) !== null));
  return { shown: categorized.slice(0, DEAL_CONTACT_TAGS_SHOWN), more: Math.max(0, categorized.length - DEAL_CONTACT_TAGS_SHOWN) };
}

/** Where the categories are written (decision 2260), under the record pages' root beside the tags. */
export const TAG_CATEGORIES_PATH = '/pages/tags/categories';
export const TAG_CATEGORY_ORDER_PATH = '/pages/tags/categories/order';
export const tagCategoryPath = (categoryId: string) => `${TAG_CATEGORIES_PATH}/${categoryId}`;
/** One record's tag, whose note is written here (decision 2261). */
export const recordTagPath = (recordType: string, recordId: string, tagId: string) => `/pages/tags/${recordType}/${recordId}/${tagId}`;
